import React, { useMemo, useState } from 'react';
import { Input, Select, Tag, Button } from 'antd';
import CommonTable from 'components/CommonTable';
import { reassignLog, ReassignEntry } from '../mockData';
import s from './s.module.less';

const typeBadgeConfig: Record<string, { color: string; label: (amount?: string) => string }> = {
    price_up: { color: 'gold', label: (amount) => `Price ↑${amount || ''}` },
    price_down: { color: 'blue', label: (amount) => `Price ↓${amount || ''}` },
    same_price: { color: 'default', label: () => 'Same price' },
    in_network: { color: 'green', label: () => 'In-network' },
    out_of_network: { color: 'red', label: () => 'Out-of-network' },
};

const statusConfig: Record<string, { color: string; label: string }> = {
    completed: { color: 'green', label: 'Completed' },
    payment_failed: { color: 'red', label: 'Payment failed' },
    pending_consent: { color: 'gold', label: 'Pending consent' },
};

const GPReassignLogPage: React.FC = () => {
    const [search, setSearch] = useState('');
    const [practiceFilter, setPracticeFilter] = useState('');

    const practiceOptions = useMemo(() => {
        const names = [...new Set(reassignLog.map((r) => r.practice))].sort();
        return [{ label: 'All practices', value: '' }, ...names.map((n) => ({ label: n, value: n }))];
    }, []);

    const filteredData = useMemo(() => {
        return reassignLog.filter((r) => {
            const matchSearch =
                !search ||
                r.patient.toLowerCase().includes(search.toLowerCase()) ||
                r.practice.toLowerCase().includes(search.toLowerCase());
            const matchPractice = !practiceFilter || r.practice === practiceFilter;
            return matchSearch && matchPractice;
        });
    }, [search, practiceFilter]);

    const columns = [
        {
            title: 'Date/Time',
            dataIndex: 'date',
            key: 'date',
            width: 180,
        },
        {
            title: 'Practice',
            dataIndex: 'practice',
            key: 'practice',
            render: (val: string) => <span style={{ fontWeight: 500 }}>{val}</span>,
        },
        {
            title: 'Admin',
            dataIndex: 'admin',
            key: 'admin',
        },
        {
            title: 'Patient',
            dataIndex: 'patient',
            key: 'patient',
        },
        {
            title: 'Original → New',
            key: 'providers',
            render: (_: unknown, record: ReassignEntry) => (
                <div>
                    <div style={{ color: '#6b7280', fontSize: 13 }}>{record.fromProvider}</div>
                    <div style={{ color: '#111827', fontSize: 13 }}>→ {record.toProvider}</div>
                </div>
            ),
        },
        {
            title: 'Type',
            key: 'type',
            render: (_: unknown, record: ReassignEntry) => {
                const config = typeBadgeConfig[record.typeBadge] || typeBadgeConfig.same_price;
                return <Tag color={config.color}>{config.label(record.amount)}</Tag>;
            },
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (val: string) => {
                const config = statusConfig[val] || { color: 'default', label: val };
                return <Tag color={config.color}>{config.label}</Tag>;
            },
        },
        {
            title: 'Action',
            key: 'action',
            render: () => (
                <Button type="link" style={{ padding: 0, color: 'var(--primary-color)' }}>
                    View details
                </Button>
            ),
        },
    ];

    return (
        <div className={s.wrap}>
            <div className={s.header}>
                <h1>Reassign Log</h1>
            </div>
            <div className={s.filters}>
                <Input
                    className={s.searchInput}
                    placeholder="Search patient name or practice..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    allowClear
                />
                <Select
                    className={s.practiceSelect}
                    value={practiceFilter}
                    onChange={setPracticeFilter}
                    options={practiceOptions}
                />
            </div>
            <CommonTable<ReassignEntry>
                data={filteredData}
                columns={columns}
                rowKey="id"
                pagination={false}
            />
        </div>
    );
};

export default GPReassignLogPage;
